/**
 * Workspace Page
 *
 * Main workspace view showing all items or filtered by folder/tag.
 * Part of the Data Workspace OS feature set.
 *
 * Features:
 * - Grid/list view of tables, linked tables, and folders
 * - Folder navigation with breadcrumbs
 * - Tag filtering
 * - Favorites, Recent, Trash views
 * - Create new items (tables, linked tables, folders)
 * - Preview panel for quick item inspection
 *
 * @module features/workspace/pages
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TableFieldApiResponse, VersionStatus } from '@loader/shared-types';

/** Minimal version shape needed for draft resume */
type VersionSummary = {
  id: string;
  status: VersionStatus;
  file_name: string;
  file_size: number;
  created_at: string;
};

import {
  FolderView,
  MoveToFolderDialog,
  CreateFolderDialog,
} from '../components';
import { useWorkspaceItems } from '../hooks/useWorkspaceItems';
import { useWorkspaceActions } from '../hooks/useWorkspaceActions';
import { usePreviewUrlSync } from '../hooks/usePreviewUrlSync';
import type { WorkspaceSearchParams } from '../utils/workspaceSearchSchema';
import { useCreateTableStore, type DraftTableData } from '../stores/useCreateTableStore';
import { useSidePanelStore } from '../stores/useSidePanelStore';

import { CreateLinkedTableDialog } from '@/features/linkedTables/shared/components';
import { useFolderQuery, useEmptyTrashMutation } from '@/features/organization/hooks';
import { ErrorBoundary, ConfirmDialog } from '@/components/common';
import { createApiError } from '@/lib/apiErrors';
import { useToastStore } from '@/store/useToasts';
import { useUndoStore } from '@/store/useUndoStore';
import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useLinkedTablesListQuery } from '@/features/linkedTables/hooks/useLinkedTableQueries';
import { useCreateTableWithLimit, useWorkspaceMembership } from '@/hooks';
import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkspacePageProps {
  /** Filter by folder ID (null = root, undefined = all) */
  folderId?: string | null;
  /** Show only favorites */
  showFavorites?: boolean;
  /** Show only recent items */
  showRecent?: boolean;
  /** Show trash items */
  showTrash?: boolean;
  /** Search params from route (for URL sync) */
  searchParams?: WorkspaceSearchParams;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Workspace page component
 *
 * Renders the main workspace view with folder navigation, filtering,
 * and item management capabilities.
 */
function WorkspacePageContent({
  folderId,
  showFavorites,
  showRecent,
  showTrash,
  searchParams,
}: WorkspacePageProps) {
  // Fetch current user's workspace role to check editor/owner access
  const { canEdit } = useWorkspaceMembership();

  // RPC client for API calls
  const rpc = useRpc();

  // Get workspace items with filtering
  const { items, isLoading } = useWorkspaceItems({
    folderId,
    showFavorites,
    showRecent,
    showTrash,
  });

  // Sync preview state with URL for shareable links
  usePreviewUrlSync({
    searchParams: searchParams || {},
  });

  // Close preview panel when folder changes
  const closeSidePanel = useSidePanelStore((s) => s.close);
  const prevFolderIdRef = useRef<string | null | undefined>(folderId);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // Skip closing panel on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevFolderIdRef.current = folderId;
      return;
    }

    // Only close panel when folder actually changes after initial mount
    if (prevFolderIdRef.current !== folderId) {
      closeSidePanel();
      prevFolderIdRef.current = folderId;
    }
  }, [folderId, closeSidePanel]);

  // Get raw tables/linkedTables data for folder ID lookup in MoveToFolderDialog
  // We need the full table/linkedTable objects to access folder_id property
  const { data: rawTables = [] } = useTablesListQuery();
  const { data: rawLinkedTables = [] } = useLinkedTablesListQuery();

  // Create table with limit check
  const { openCreateTable, LimitDialog } = useCreateTableWithLimit();
  const resumeDraft = useCreateTableStore((s) => s.resumeDraft);
  const [isResumingDraft, setIsResumingDraft] = useState(false);

  // Get all action handlers and dialog state
  const {
    dialogState,
    selectedItem,
    selectedItems,
    deleteConfirmItem,
    permanentDeleteConfirmItem,
    cascadeDeleteState,
    isCreatingLinkedTable,
    openCreateLinkedTableDialog,
    closeCreateLinkedTableDialog,
    openCreateFolderDialog,
    closeCreateFolderDialog,
    closeMoveDialog,
    closeDeleteConfirmDialog,
    closePermanentDeleteConfirmDialog,
    closeCascadeDeleteDialog,
    handleCreateLinkedTable,
    handleDuplicate,
    handleToggleFavorite,
    handleMove,
    handleMoveComplete,
    handleDelete,
    confirmDelete,
    handleRestore,
    handleDeletePermanently,
    confirmPermanentDelete,
    confirmCascadeDelete,
    openPreview,
  } = useWorkspaceActions({ folderId });

  /**
   * Handle create table click - opens the sheet with limit check
   */
  const handleCreateTable = () => {
    openCreateTable(folderId);
  };

  // Get folder details for title
  const { data: folder } = useFolderQuery(folderId ?? undefined);

  // Empty trash mutation
  const emptyTrashMutation = useEmptyTrashMutation();

  const queryClient = useQueryClient();
  const { error: showError } = useToastStore();
  const { executeUndo, canUndo } = useUndoStore();

  /**
   * Handle resume draft table - fetches table data and opens sheet to continue setup
   * Uses queryClient.fetchQuery() to leverage TanStack Query caching
   */
  const handleResumeDraft = useCallback(async (tableId: string) => {
    if (isResumingDraft) return;
    setIsResumingDraft(true);

    try {
      // Fetch table details using TanStack Query for caching
      const table = await queryClient.fetchQuery({
        queryKey: queryKeys.tables.detail(tableId),
        queryFn: async () => {
          // @ts-expect-error - Hono RPC type inference limitation
          const res = await rpc.api.private.tables[':id'].$get({
            param: { id: tableId },
          });
          if (!res.ok) throw await createApiError(res, 'table');
          const data = await res.json();
          return data.table;
        },
      });

      // Fetch versions using TanStack Query for caching
      // Need id, status, file_name and file_size for resuming draft
      const versions = await queryClient.fetchQuery({
        queryKey: queryKeys.tables.versions(tableId),
        queryFn: async () => {
          const res = await rpc.api.private.versions.$get({
            query: { table_id: tableId },
          });
          if (!res.ok) throw await createApiError(res, 'versions');
          const data = await res.json();
          return (data.data || []) as VersionSummary[];
        },
      });

      // Find the most recent version (likely the draft being worked on)
      const latestVersion = versions[0] as VersionSummary | undefined;

      // Map fields to CreateTableFieldConfig format using shared types
      const fields = ((table.fields || []) as TableFieldApiResponse[]).map((field) => ({
        id: field.id,
        name: field.name,
        dataType: field.data_type,
        isRequired: field.is_required,
        isUnique: field.is_unique,
        description: field.description || undefined,
      }));

      // Prepare draft data
      const draftData: DraftTableData = {
        tableId: table.id,
        tableName: table.name,
        folderId: table.folder_id || null,
        versionId: latestVersion?.id || null,
        versionStatus: latestVersion?.status || null,
        fields,
        fileName: latestVersion?.file_name || null,
        fileSize: latestVersion?.file_size || null,
        versionCreatedAt: latestVersion?.created_at ?? null,
      };

      // Open sheet with draft data
      resumeDraft(draftData);
    } catch (err) {
      showError('Failed to resume draft', err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsResumingDraft(false);
    }
  }, [queryClient, rpc, resumeDraft, showError, isResumingDraft]);

  /**
   * Handle keyboard shortcuts
   * - Cmd/Ctrl+Z: Undo
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Accept both Cmd and Ctrl on all platforms for cross-platform compatibility
      const isModKey = e.metaKey || e.ctrlKey;

      if (!isModKey) return;

      // Undo
      if (e.key === 'z' || e.key === 'Z') {
        if (canUndo()) {
          e.preventDefault();
          void executeUndo().catch((err) => {
            showError('Failed to undo', err instanceof Error ? err.message : 'Unknown error');
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, executeUndo, showError]);

  /**
   * Get page title based on current view
   */
  const getPageTitle = (): string | undefined => {
    if (showFavorites) return 'Favorites';
    if (showRecent) return 'Recent';
    if (showTrash) return 'Trash';
    if (folderId && folder) return folder.name;
    if (folderId) return 'Folder';
    return undefined; // Will show "All Items" in FolderView
  };

  return (
    <>
      {/* Main content */}
      <div className="absolute inset-4 top-0 flex flex-col overflow-hidden">
        <FolderView
          items={items}
          isLoading={isLoading}
          folderId={folderId}
          folderName={getPageTitle()}
          isTrash={showTrash}
          canEdit={canEdit}
          onEmptyTrash={() => emptyTrashMutation.mutate()}
          isEmptyingTrash={emptyTrashMutation.isPending}
          onCreateTable={handleCreateTable}
          onCreateLinkedTable={openCreateLinkedTableDialog}
          onCreateFolder={openCreateFolderDialog}
          onDuplicate={handleDuplicate}
          onToggleFavorite={handleToggleFavorite}
          onMove={handleMove}
          onDelete={showTrash ? handleRestore : handleDelete}
          onRestore={handleRestore}
          onDeletePermanently={handleDeletePermanently}
          onPreview={openPreview}
          onResumeDraft={handleResumeDraft}
        />
      </div>

      {/* Create Linked Table Dialog */}
      <CreateLinkedTableDialog
        isOpen={dialogState.createLinkedTable}
        onClose={closeCreateLinkedTableDialog}
        onCreateLinkedTable={handleCreateLinkedTable}
        isCreating={isCreatingLinkedTable}
      />

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        isOpen={dialogState.createFolder}
        onClose={closeCreateFolderDialog}
        parentFolderId={folderId}
      />

      {/* Move to Folder Dialog */}
      {(selectedItem || selectedItems.length > 0) && (
        <MoveToFolderDialog
          isOpen={dialogState.moveToFolder}
          onClose={closeMoveDialog}
          items={
            selectedItems.length > 0
              ? selectedItems
              : selectedItem
                ? [{ id: selectedItem.id, type: selectedItem.type }]
                : []
          }
          currentFolderId={
            // For bulk moves, we don't track original folder per item (simplified)
            selectedItem?.type === 'table'
              ? rawTables.find(t => t.id === selectedItem.id)?.folder_id
              : selectedItem?.type === 'linked_table'
              ? rawLinkedTables.find(lt => lt.id === selectedItem?.id)?.folder_id
              : null
          }
          onMoveComplete={handleMoveComplete}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={dialogState.deleteConfirm}
        onOpenChange={(open) => {
          if (!open) closeDeleteConfirmDialog();
        }}
        title="Move to Trash"
        description={`Are you sure you want to move ${deleteConfirmItem?.displayName ?? 'this item'} to trash? You can restore it later from the trash.`}
        confirmText="Move to Trash"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={confirmDelete}
      />

      {/* Permanent Delete Confirmation Dialog */}
      <ConfirmDialog
        open={dialogState.permanentDeleteConfirm}
        onOpenChange={(open) => {
          if (!open) closePermanentDeleteConfirmDialog();
        }}
        title="Delete Permanently"
        description={`Are you sure you want to permanently delete ${permanentDeleteConfirmItem?.displayName ?? 'this item'}? This action cannot be undone.`}
        confirmText="Delete Permanently"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={confirmPermanentDelete}
      />

      {/* Cascade Delete Confirmation Dialog */}
      <ConfirmDialog
        open={dialogState.cascadeDeleteConfirm}
        onOpenChange={(open) => {
          if (!open) closeCascadeDeleteDialog();
        }}
        title="Delete Table and Dependencies"
        description={
          cascadeDeleteState
            ? `Deleting "${cascadeDeleteState.itemName}" will also permanently delete the following linked tables that depend on it:\n\n${cascadeDeleteState.dependentLinkedTables.map((lt) => `• ${lt.name}`).join('\n')}\n\nThis action cannot be undone.`
            : 'This action cannot be undone.'
        }
        confirmText="Delete All"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={confirmCascadeDelete}
      />

      {/* Table Limit Dialog */}
      <LimitDialog />
    </>
  );
}

export function WorkspacePage(props: WorkspacePageProps) {
  return (
    <ErrorBoundary>
      <WorkspacePageContent {...props} />
    </ErrorBoundary>
  );
}
